/**
 * Who wrote this, decided in one place.
 *
 * The group's rule is that nothing AI-produced may look like a fact from the
 * document or like a person's own words. That rule is enforced by a handful of
 * conditionals, and a conditional buried in JSX is a conditional nobody tests —
 * so the two that decide whether a stamp says "◔ drafted by software" or says
 * nothing at all live here, as functions, with a test beside them.
 *
 * The state they encode is not binary. A claim's wording can be a rep's own
 * sentence awaiting a second reader, or a model's sentence nobody has touched,
 * or a model's sentence a person has rewritten. Those are three different risks
 * and the workbench showed one label for all three.
 */

import type { DraftedText as MachineDraft } from '../lib/api.js';

/**
 * A model's draft of one claim field, as `POST /claims/:id/draft` returns it.
 *
 * Re-exported rather than restated. A second structurally identical interface
 * here would keep compiling long after the route stopped sending one of these
 * fields, and the field most likely to go is `seeded` — which is the one that
 * decides whether the stamp says a person's words were developed or that nobody
 * wrote any of this.
 */
export type { MachineDraft };

/**
 * Whether the text on screen is still exactly what came back from the model.
 *
 * Compared on the trimmed text rather than tracked with a flag, because a flag
 * has to be cleared by whoever writes the next onChange handler and this must
 * not depend on that. Trailing whitespace is not an edit; a single changed
 * character is.
 */
export function isUntouched(value: string, machine: MachineDraft | null): boolean {
  return machine !== null && value.trim() === machine.text.trim();
}

/**
 * What the stamp beside an unsaved draft should say.
 *
 * Three sentences for three states, and the ◔ — the glyph that means unchecked
 * everywhere else in this product — only on the one where no person has read a
 * word of it. Once someone edits, the text is theirs and the stamp records
 * where it started rather than warning about it.
 */
export function draftStamp(
  value: string,
  machine: MachineDraft | null,
): { text: string; unchecked: boolean } | null {
  if (!machine) return null;
  if (!isUntouched(value, machine)) {
    return { text: `edited by you, from a ${machine.model} draft`, unchecked: false };
  }
  return {
    text: machine.seeded
      ? `developed from your words by ${machine.model} · nobody has checked it`
      : `drafted by ${machine.model} · nobody has written or checked this`,
    unchecked: true,
  };
}

/**
 * What the stamp beside a *saved* proposed wording should say.
 *
 * The machine draft has been kept in `proposed_wording_ai` since migration 0006
 * and nothing had ever read it, so a rep's own sentence and unedited model
 * output both displayed "not yet checked word for word". They are not the same
 * claim about the world: one has an author.
 *
 * Null when there is nothing to say — no wording at all, or a person has
 * already checked it word for word and the verified stamp takes over.
 */
export function savedWordingStamp(claim: {
  proposedWording: string | null;
  proposedWordingAi: string | null;
  wordingVerifiedAt: string | null;
}): string | null {
  if (claim.wordingVerifiedAt !== null) return null;
  if (!claim.proposedWording) return null;
  if (claim.proposedWordingAi === null) return 'not yet checked word for word';
  return claim.proposedWordingAi === claim.proposedWording
    ? 'drafted by software, unedited · not checked word for word'
    : 'edited by a person from a software draft · not checked word for word';
}
